import { CHAT_IMAGE_MAX_BYTES, CHAT_IMAGES_PER_MESSAGE, ChatImageType } from "@kwp/contracts";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState, type ClipboardEvent, type DragEvent } from "react";
import { authHeaders, type TokenSource } from "../../lib/api";
import { firebaseTokenSource } from "../../lib/auth-token";
import { useClients } from "../../lib/clients";
import { IconButton } from "../../ui/icons";

/**
 * Images shared in chat (WP-058, architecture §11). The bytes go up and come back through the api
 * (`/chat/images/{workspaceId}/{channelId}[/{imageId}]`) with the bearer token in a header, so the
 * token never goes in a URL; a fetched image is shown from a blob. A message names its images by
 * the ids the uploads returned.
 */
export const ACCEPTED_TYPES: readonly string[] = ChatImageType.options;

export function chatImagesUrl(apiUrl: string, workspaceId: string, channelId: string, imageId?: string): string {
  const base = `${apiUrl.replace(/\/+$/, "")}/chat/images/${encodeURIComponent(workspaceId)}/${encodeURIComponent(channelId)}`;
  return imageId === undefined ? base : `${base}/${encodeURIComponent(imageId)}`;
}

const FAILURES: Record<string, string> = {
  image_type: "Only PNG, JPEG, GIF and WebP images can be shared.",
  image_too_large: "Images can be at most 10 MB.",
  image_empty: "That image is empty.",
  chat_images_off: "Images are not available here yet.",
};

async function failureOf(res: Response, fallback: string): Promise<Error> {
  const body = (await res.json().catch(() => null)) as { error?: { reason?: string } } | null;
  const reason = body?.error?.reason;
  const known = reason === undefined ? undefined : FAILURES[reason];
  return new Error(known ?? `${fallback} (${res.status}).`);
}

/** The files to add and, when some were left out, why. Checked here so nothing doomed is sent. */
export function acceptFiles(files: readonly File[], already: number): { files: File[]; error: string | null } {
  const images = files.filter((f) => ACCEPTED_TYPES.includes(f.type));
  const small = images.filter((f) => f.size <= CHAT_IMAGE_MAX_BYTES);
  const room = Math.max(CHAT_IMAGES_PER_MESSAGE - already, 0);
  const kept = small.slice(0, room);
  const error =
    images.length < files.length
      ? FAILURES["image_type"]
      : small.length < images.length
        ? FAILURES["image_too_large"]
        : kept.length < small.length
          ? `At most ${CHAT_IMAGES_PER_MESSAGE} images per message.`
          : null;
  return { files: kept, error: error ?? null };
}

/** The image's size in pixels, for laying it out before it loads. */
export async function measureImage(file: Blob): Promise<{ width: number; height: number }> {
  const bitmap = await createImageBitmap(file);
  const size = { width: bitmap.width, height: bitmap.height };
  bitmap.close();
  return size;
}

export async function uploadChatImage(
  fetcher: typeof fetch,
  apiUrl: string,
  getToken: TokenSource,
  target: { workspaceId: string; channelId: string },
  file: File,
): Promise<{ id: string; width: number; height: number }> {
  const [size, res] = await Promise.all([
    measureImage(file),
    fetcher(chatImagesUrl(apiUrl, target.workspaceId, target.channelId), {
      method: "POST",
      headers: { ...(await authHeaders(getToken)), "content-type": "application/octet-stream" },
      body: file,
    }),
  ]);
  if (!res.ok) throw await failureOf(res, "The image could not be sent");
  const { id } = (await res.json()) as { id: string };
  return { id, ...size };
}

export async function fetchChatImage(fetcher: typeof fetch, apiUrl: string, getToken: TokenSource, workspaceId: string, channelId: string, imageId: string): Promise<Blob> {
  const res = await fetcher(chatImagesUrl(apiUrl, workspaceId, channelId, imageId), { headers: await authHeaders(getToken) });
  if (!res.ok) throw await failureOf(res, "The image could not be loaded");
  return res.blob();
}

/** An object URL for a blob, revoked when the blob changes or the component goes. */
export function useObjectUrl(blob: Blob | null | undefined): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (blob === null || blob === undefined) {
      setUrl(null);
      return undefined;
    }
    const made = URL.createObjectURL(blob);
    setUrl(made);
    return () => URL.revokeObjectURL(made);
  }, [blob]);
  return url;
}

export function useChatImage(workspaceId: string, channelId: string, imageId: string) {
  const { config, firebase } = useClients();
  // An image never changes, so one fetch per session is enough.
  return useQuery({
    queryKey: ["chat-image", workspaceId, channelId, imageId],
    queryFn: () => fetchChatImage(window.fetch.bind(window), config.apiUrl, firebaseTokenSource(firebase.auth), workspaceId, channelId, imageId),
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: 30 * 60_000,
  });
}

/** The images waiting in the composer, each with a way to take it out again. */
export function AttachmentTray({ files, onRemove }: { files: readonly File[]; onRemove: (index: number) => void }) {
  if (files.length === 0) return null;
  return (
    <ul aria-label="Images to send" className="flex flex-wrap gap-2 px-2 pt-2">
      {files.map((file, i) => (
        <AttachmentPreview key={`${file.name}-${file.size}-${file.lastModified}-${i}`} file={file} index={i} onRemove={onRemove} />
      ))}
    </ul>
  );
}

function AttachmentPreview({ file, index, onRemove }: { file: File; index: number; onRemove: (index: number) => void }) {
  const url = useObjectUrl(file);
  return (
    <li className="relative">
      {url === null ? null : <img src={url} alt={file.name} className="size-16 rounded-md border border-border object-cover" />}
      <button
        type="button"
        onClick={() => onRemove(index)}
        aria-label={`Remove ${file.name}`}
        className="absolute -right-2 -top-2 grid size-6 place-items-center rounded-full border border-border bg-surface text-xs text-muted shadow hover:text-text"
      >
        ×
      </button>
    </li>
  );
}

/** The composer's images waiting to be sent, checked as they are added. */
export function useAttachments() {
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);
  return {
    files,
    error,
    add(list: readonly File[]) {
      if (list.length === 0) return;
      const accepted = acceptFiles(list, files.length);
      setFiles([...files, ...accepted.files]);
      setError(accepted.error);
    },
    remove(index: number) {
      setFiles(files.filter((_, i) => i !== index));
      setError(null);
    },
    clear() {
      setFiles([]);
      setError(null);
    },
    fail: setError,
  };
}

/** A button that opens the file picker for images (several at once). */
export function AttachButton({ onFiles }: { onFiles: (files: File[]) => void }) {
  const input = useRef<HTMLInputElement>(null);
  return (
    <>
      <IconButton icon="image" label="Add images" className="mb-0.5" onClick={() => input.current?.click()} />
      <input
        ref={input}
        type="file"
        accept={ACCEPTED_TYPES.join(",")}
        multiple
        hidden
        data-testid="chat-image-input"
        onChange={(e) => {
          onFiles([...(e.currentTarget.files ?? [])]);
          e.currentTarget.value = "";
        }}
      />
    </>
  );
}

/** Lets images be dropped on an element. */
export function imageDropProps(add: (files: File[]) => void) {
  return {
    onDragOver: (e: DragEvent) => e.preventDefault(),
    onDrop: (e: DragEvent) => {
      e.preventDefault();
      add([...e.dataTransfer.files]);
    },
  };
}

/** Takes pasted files as images; plain pasted text is left to the text box. */
export function pasteImages(e: ClipboardEvent, add: (files: File[]) => void): void {
  const pasted = [...e.clipboardData.files];
  if (pasted.length === 0) return;
  e.preventDefault();
  add(pasted);
}
